// src/contexts/AppContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
  appointmentsService, proceduresService, usersService,
  notificationsService, scheduleWindowsService, businessConfigService,
  establishmentsService,
  DEFAULT_BUSINESS_CONFIG,
} from 'infrastructure/firebase';
import { sendAppointmentReminder } from 'infrastructure/emailService';
import { isAdminRole } from 'shared/config/roles';

const AppContext = createContext();

// ─── Constantes de tempo ──────────────────────────────────────────────────────
const BRAZIL_OFFSET_MS = 3 * 60 * 60 * 1000;  // UTC-3 (Brasília)
const H24              = 24 * 60 * 60 * 1000;
const H5               = 5  * 60 * 60 * 1000;
const WINDOW_MS        = 30 * 60 * 1000;        // tolerância ±30 min por ciclo

/**
 * Converte data/hora do agendamento (horário de Brasília) para timestamp UTC.
 * Exemplo: "2026-05-30" + "14:00" (Brasil UTC-3) → UTC timestamp das 17:00
 */
const apptToUTC = (date, time) => {
  const [y, mo, d]  = date.split('-').map(Number);
  const [h, mi = 0] = (time || '00:00').split(':').map(Number);
  return Date.UTC(y, mo - 1, d, h, mi) + BRAZIL_OFFSET_MS;
};

/**
 * Verifica agendamentos próximos e cria notificações/atualiza status quando necessário.
 * Janela 24h: cria notificação reminder24h + envia e-mail de lembrete.
 * Janela 5h : cria notificação confirm + muda status para waitingConfirm.
 * A verificação de notificações existentes evita duplicatas.
 */
// `firedSet` (Set em ref) evita escrever a mesma notificação várias vezes durante a
// latência entre o write e o snapshot atualizar `existingNotifs` — antes isso gerava
// rajadas de escritas duplicadas no Firestore a cada atualização de agendamentos.
const checkAppointmentReminders = async (myAppts, existingNotifs, userProfile, firedSet) => {
  if (!userProfile?.id) return;
  const now = Date.now();

  for (const appt of myAppts) {
    if (!appt.date || !appt.time) continue;
    if (['cancelled', 'done', 'confirmed'].includes(appt.status)) continue;

    const apptUTC = apptToUTC(appt.date, appt.time);
    const diff    = apptUTC - now;

    const in24h = diff >= H24 - WINDOW_MS && diff <= H24 + WINDOW_MS;
    const in5h  = diff >= H5  - WINDOW_MS && diff <= H5  + WINDOW_MS;

    if (!in24h && !in5h) continue;

    const key24 = `${appt.id}:reminder24h`;
    const key5  = `${appt.id}:confirm`;

    const has24h = firedSet?.has(key24) || existingNotifs.some(
      n => n.appointmentId === appt.id && n.type === 'reminder24h'
    );
    const has5h = firedSet?.has(key5) || existingNotifs.some(
      n => n.appointmentId === appt.id && n.type === 'confirm'
    );

    // ── Janela 24h: lembrete de agendamento amanhã ──────────────────────────
    if (in24h && !has24h) {
      firedSet?.add(key24);
      notificationsService
        .add({
          userId:          userProfile.id,
          type:            'reminder24h',
          appointmentId:   appt.id,
          title:           'Lembrete: agendamento amanhã',
          message:         `Você tem ${appt.procedureName} com ${appt.professionalName} amanhã às ${appt.time}. Confirme sua presença no app.`,
          appointmentDate: appt.date,
          appointmentTime: appt.time,
        })
        .then(() => {
          // E-mail de lembrete (não bloqueia)
          if (userProfile.email) {
            sendAppointmentReminder({
              clientName:       userProfile.name,
              clientEmail:      userProfile.email,
              professionalName: appt.professionalName,
              procedureName:    appt.procedureName,
              date:             appt.date,
              time:             appt.time,
            }).catch(err => console.warn('[Reminder24h] E-mail:', err?.message));
          }
        })
        .catch(err => console.warn('[Reminder24h] Notificação:', err?.message));
    }

    // ── Janela 5h: confirmação de presença hoje ──────────────────────────────
    if (in5h && !has5h) {
      firedSet?.add(key5);
      Promise.all([
        notificationsService.add({
          userId:          userProfile.id,
          type:            'confirm',
          appointmentId:   appt.id,
          title:           'Confirme sua presença',
          message:         `Você tem ${appt.procedureName} com ${appt.professionalName} hoje às ${appt.time}. Confirme sua presença no app.`,
          appointmentDate: appt.date,
          appointmentTime: appt.time,
        }),
        appointmentsService.setWaitingConfirm(appt.id),
      ]).then(() => {
        // E-mail de confirmação 5h antes
        if (userProfile.email) {
          sendAppointmentReminder({
            clientName:       userProfile.name,
            clientEmail:      userProfile.email,
            professionalName: appt.professionalName,
            procedureName:    appt.procedureName,
            date:             appt.date,
            time:             appt.time,
          }).catch(err => console.warn('[Confirm5h] E-mail:', err?.message));
        }
      }).catch(err => console.warn('[Confirm5h] Notificação/status:', err?.message));
    }
  }
};

// ─────────────────────────────────────────────────────────────────────────────

export function AppProvider({ children }) {
  const { userProfile, estabelecimentoId, isSuperAdmin } = useAuth();
  const [appointments, setAppointments]       = useState([]);
  const [procedures, setProcedures]           = useState([]);
  const [professionals, setProfessionals]     = useState([]);
  const [clients, setClients]                 = useState([]);
  const [notifications, setNotifications]     = useState([]);
  const [scheduleWindows, setScheduleWindows] = useState([]);
  const [businessConfig, setBusinessConfig]   = useState(DEFAULT_BUSINESS_CONFIG);
  const [establishment, setEstablishment]     = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [toast, setToast]                     = useState(null);

  // Refs para o setInterval e visibilitychange sempre lerem dados atualizados
  // sem precisar recriar o intervalo a cada mudança de estado.
  const appointmentsRef  = useRef(appointments);
  const notificationsRef = useRef(notifications);
  const userProfileRef   = useRef(userProfile);
  // Chaves de lembretes já disparados nesta sessão (evita writes duplicados)
  const firedRemindersRef = useRef(new Set());

  useEffect(() => { appointmentsRef.current  = appointments;  }, [appointments]);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  useEffect(() => { userProfileRef.current   = userProfile;   }, [userProfile]);

  // ── Carrega dados do Firestore ─────────────────────────────────────────────
  useEffect(() => {
    if (!userProfile) {
      setAppointments([]);
      setProcedures([]);
      setProfessionals([]);
      setClients([]);
      setNotifications([]);
      setScheduleWindows([]);
      setBusinessConfig(DEFAULT_BUSINESS_CONFIG);
      setEstablishment(null);
      setLoading(false);
      return;
    }

    const uid    = userProfile.id;
    const role   = userProfile.role;
    const estabId = estabelecimentoId; // null = super admin, string = clinic admin / professional
    const unsubs  = [];

    const onErr = (label) => (err) => {
      console.error(`[Firestore] Erro em "${label}":`, err?.message || err);
      if (err?.message?.includes('indexes?create_composite')) {
        console.error('[Firestore] 👆 Clique no link acima para criar o índice necessário no Firebase Console.');
      }
    };

    // Carrega o estabelecimento vinculado ao usuário (clínica admin ou profissional)
    if (estabId) {
      establishmentsService.getById(estabId)
        .then(setEstablishment)
        .catch(() => setEstablishment(null));
    }

    businessConfigService.get().then(setBusinessConfig).catch(() => {});
    unsubs.push(proceduresService.onSnapshot(setProcedures, onErr('procedimentos')));
    unsubs.push(scheduleWindowsService.onSnapshot(setScheduleWindows, onErr('janelaAgenda')));
    unsubs.push(notificationsService.onSnapshotByClient(uid, setNotifications, onErr('notificacoes')));

    if (isAdminRole(role)) {
      if (isSuperAdmin) {
        // Super admin (somente SUPER_ADMIN_EMAIL): vê todos os agendamentos e profissionais
        unsubs.push(appointmentsService.onSnapshot(setAppointments, {}, onErr('agendamentos')));
        unsubs.push(usersService.onSnapshot((users) => {
          setProfessionals(users.filter(u => u.role === 'professional'));
          setClients(users.filter(u => u.role === 'client'));
        }, null, onErr('usuarios')));
      } else if (estabId) {
        // Admin de clínica: agendamentos e profissionais do SEU estabelecimento.
        // Clientes: todos (necessário para criar agendamentos de qualquer cliente).
        unsubs.push(appointmentsService.onSnapshot(setAppointments, { estabelecimentoId: estabId }, onErr('agendamentos')));
        unsubs.push(usersService.onSnapshot((users) => {
          setProfessionals(users.filter(u => u.role === 'professional' && u.estabelecimentoId === estabId));
          setClients(users.filter(u => u.role === 'client'));
        }, null, onErr('usuarios')));
      } else {
        // SEGURANÇA: admin sem estabelecimento e que NÃO é super admin = estado
        // inconsistente (ex.: cadastro interrompido). Não carrega dados sensíveis.
        setAppointments([]);
        setProfessionals([]);
        setClients([]);
      }
    } else if (role === 'professional') {
      unsubs.push(appointmentsService.onSnapshot(setAppointments, { professionalId: uid }, onErr('agendamentos')));
      unsubs.push(usersService.onSnapshot(setClients, 'client', onErr('usuarios-client')));
      unsubs.push(usersService.onSnapshot(setProfessionals, 'professional', onErr('usuarios-professional')));
    } else {
      // Cliente: carrega apenas seus próprios agendamentos
      unsubs.push(appointmentsService.onSnapshot(setAppointments, { clientId: uid }, onErr('agendamentos')));
      unsubs.push(usersService.onSnapshot(setProfessionals, 'professional', onErr('usuarios-professional-client')));
    }

    setLoading(false);
    return () => unsubs.forEach(u => u && u());
  }, [userProfile, estabelecimentoId, isSuperAdmin]); // eslint-disable-line

  // ── Verificação de lembretes — roda ao carregar/atualizar agendamentos ──────
  // Cobre o caso "ao abrir o app" e "quando um agendamento novo entra".
  useEffect(() => {
    if (!userProfile || userProfile.role !== 'client') return;
    const pending = appointments.filter(
      a => a.clientId === userProfile.id && a.status === 'pending'
    );
    if (pending.length === 0) return;
    checkAppointmentReminders(pending, notifications, userProfile, firedRemindersRef.current);
  }, [appointments, notifications, userProfile]);

  // ── Intervalo: roda a cada 30 min enquanto a aba está aberta ─────────────
  useEffect(() => {
    if (!userProfile || userProfile.role !== 'client') return;

    const run = () => {
      const pending = appointmentsRef.current.filter(
        a => a.clientId === userProfileRef.current?.id && a.status === 'pending'
      );
      if (pending.length > 0) {
        checkAppointmentReminders(pending, notificationsRef.current, userProfileRef.current, firedRemindersRef.current);
      }
    };

    const interval = setInterval(run, 30 * 60 * 1000);

    // Roda também quando o usuário volta para a aba (visibilidade)
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userProfile]); // só recria se o usuário mudar

  // ── Helpers ────────────────────────────────────────────────────────────────
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const refreshBusinessConfig = useCallback(async () => {
    try {
      const cfg = await businessConfigService.get();
      setBusinessConfig(cfg);
    } catch {
      setBusinessConfig(DEFAULT_BUSINESS_CONFIG);
    }
  }, []);

  const dismissNotification = useCallback(async (id) => {
    await notificationsService.dismiss(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const uid  = userProfile?.id;
  const role = userProfile?.role;

  // ── Valores derivados memoizados ───────────────────────────────────────────
  // Sem useMemo, todos estes filtros O(n) re-executavam em CADA render, e o objeto
  // de contexto era recriado a cada render — forçando todos os consumidores
  // (todas as páginas + Layout) a re-renderizar a cada snapshot do Firestore.

  // myAppointments: filtrado pelo uid do usuário conforme o papel
  const myAppointments = useMemo(() =>
    role === 'client'       ? appointments.filter(a => a.clientId       === uid) :
    role === 'professional' ? appointments.filter(a => a.professionalId === uid) :
    appointments,
  [appointments, role, uid]);

  // Todos os perfis administrativos/profissionais vêem todos os clientes cadastrados.
  const estabClients = clients;

  const todayAppointments = useMemo(() => {
    const _now = new Date();
    const today = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
    return appointments.filter(a => {
      if (a.dateTime?.toDate) {
        const dt = a.dateTime.toDate();
        const dStr = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
        return dStr === today;
      }
      return (a.date || '') === today;
    });
  }, [appointments]);

  // ── Visibilidade de procedimentos por perfil ──────────────────────────────
  const visibleProcedures = useMemo(() => {
    if (!role || role === 'client') return procedures; // cliente: catálogo global
    if (isAdminRole(role) && isSuperAdmin) return procedures; // super admin (e-mail): tudo
    if (isAdminRole(role)) {
      // Admin de clínica: apenas procedimentos do seu estabelecimento (vazio se sem estab)
      return procedures.filter(p => p.estabelecimentoId === estabelecimentoId);
    }
    if (role === 'professional' && estabelecimentoId) {
      return procedures.filter(p => p.estabelecimentoId === estabelecimentoId);
    }
    if (role === 'professional' && !estabelecimentoId) {
      return procedures.filter(p => {
        const ids = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
        return ids.includes(uid);
      });
    }
    return procedures;
  }, [procedures, role, isSuperAdmin, estabelecimentoId, uid]);

  const pendingProcedures    = useMemo(() => visibleProcedures.filter(p => !p.approved), [visibleProcedures]);
  const approvedProcedures   = useMemo(() => visibleProcedures.filter(p =>  p.approved), [visibleProcedures]);
  const pendingConfirmations = useMemo(() => myAppointments.filter(a => a.status === 'waitingConfirm'), [myAppointments]);
  const pendingWindows       = useMemo(() => scheduleWindows.filter(w => w.status === 'pending'), [scheduleWindows]);

  // Objeto de contexto memoizado — só muda quando algo realmente muda.
  const value = useMemo(() => ({
    appointments, myAppointments,
    procedures: visibleProcedures,
    professionals, clients, estabClients, notifications,
    scheduleWindows, pendingWindows, businessConfig, establishment,
    loading, toast, showToast, dismissNotification, refreshBusinessConfig,
    todayAppointments, pendingProcedures, approvedProcedures, pendingConfirmations,
  }), [
    appointments, myAppointments, visibleProcedures, professionals, clients, estabClients,
    notifications, scheduleWindows, pendingWindows, businessConfig, establishment,
    loading, toast, showToast, dismissNotification, refreshBusinessConfig,
    todayAppointments, pendingProcedures, approvedProcedures, pendingConfirmations,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp deve ser usado dentro de AppProvider');
  return ctx;
};
